import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { useNavigate, useParams } from "react-router-dom";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { allCategories, createBlog, getBlogById, updateBlog } from "../../../utils/api";
import { Modal, Button, Spinner } from "react-bootstrap";

function DraftComponent() {
    const [categories, setCategories] = useState([]);
    const [editorLoaded, setEditorLoaded] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isPLoading, setIsPLoading] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [imagePreview, setImagePreview] = useState(null);
    const [publishStatus, setPublishStatus] = useState("draft"); 

    const [initialValues, setInitialValues] = useState({
        title: "",
        category: "",
        blogDate: "",
        images: null,
        content: "",
    });

    const { blogId } = useParams();
    const navigate = useNavigate();

    useEffect(() => {
        const fetchCategories = async () => {
            const allcat = await allCategories();
            if (allcat) {
                setCategories(allcat.data.data);
            }
            setEditorLoaded(true);
        };

        const fetchBlogData = async () => {
            if (blogId) {
                const response = await getBlogById(blogId);
                if (response.data.status && response.data.detail.publish_status === "draft") {
                    setInitialValues({
                        title: response.data.detail.title,
                        category: response.data.detail.category_id,
                        blogDate: response.data.detail.blog_date,
                        images: response.data.detail.image,
                        content: response.data.detail.content,
                    });

                    if (response.data.detail.image) {
                        let baseurl = `${process.env.REACT_APP_SOCKET_URL}`;
                        setImagePreview(`${baseurl}/upload/cab/resized/${response.data.detail.image}`);
                    }
                } else {
                    toast.warning("Blog not in draft state!");
                    navigate("/coach/my-blogs");
                }
            }
        };

        fetchCategories();
        fetchBlogData();
    }, [blogId]);

    const validationSchema = Yup.object().shape({
        title: Yup.string()
            .required("Title is required")
            .min(5, "Title must be at least 5 characters"),
        category: Yup.string().required("Category is required"),
        blogDate: Yup.date().required("Date is required"),
        images: Yup.mixed(),
        content: Yup.string().required("Content is required"),
    });

    const handleSubmit = async (values) => {
        publishStatus === "published" ? setIsPLoading(true) : setIsLoading(true);

        const formData = new FormData();
        formData.append("title", values.title);
        formData.append("category_id", values.category);
        formData.append("blog_date", values.blogDate);
        if (values.images) formData.append("images", values.images);
        formData.append("content", values.content);
        formData.append("publish_status", publishStatus);

        let response;
        if (blogId) {
            formData.append('id', blogId);
            response = await updateBlog(formData);
        } else {
            response = await createBlog(formData);
        }

        publishStatus === "published" ? setIsPLoading(false) : setIsLoading(false);

        if (response.data.status) {
            if (publishStatus === "published") {
                setShowModal(true);
            } else {
                toast.success(blogId ? "Blog updated successfully" : "Blog saved successfully");
                navigate("/coach/my-blogs");
            }
        } else {
            toast.error("Something went wrong");
        }
    };

    return (
        <div className="bg-body-tertiary p-4">
            <h2 className="text-center mb-4">{blogId ? "Edit Blog" : "Create Blog"}</h2>
            <Formik
                initialValues={initialValues}
                enableReinitialize
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
            >
                {({ setFieldValue, values }) => (
                    <Form>
                        {/* Title */}
                        <div className="mb-3">
                            <label className="form-label fw-500">Title</label>
                            <Field type="text" name="title" className="form-control fs-14" placeholder="Enter title" />
                            <ErrorMessage name="title" component="div" className="text-danger fs-12" />
                        </div>

                        {/* Category */}
                        <div className="mb-3">
                            <label className="form-label fw-500">Category</label>
                            <Field as="select" name="category" className="form-control fs-14">
                                <option value="">Select</option>
                                {categories.map((cat) => (
                                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                                ))}
                            </Field>
                            <ErrorMessage name="category" component="div" className="text-danger fs-12" />
                        </div>

                        {/* Blog Date */}
                        <div className="mb-3">
                            <label className="form-label fw-500">Blog Date</label>
                            <Field type="date" name="blogDate" className="form-control fs-14" />
                            <ErrorMessage name="blogDate" component="div" className="text-danger fs-12" />
                        </div>

                        {/* Blog Image */}
                        <div className="mb-3">
                            <label className="form-label fw-500">Blog Image</label>
                            <input 
                                type="file" 
                                accept="image/png, image/jpeg" 
                                className="form-control fs-14" 
                                onChange={(event) => {
                                    const file = event.target.files[0];
                                    if (file) {
                                        setFieldValue("images", file);
                                        setImagePreview(URL.createObjectURL(file));
                                    }
                                }} 
                            />
                            <ErrorMessage name="images" component="div" className="text-danger fs-12" />
                            {imagePreview && (
                                <div className="mb-2">
                                    <img src={imagePreview} alt="Blog Preview" width="150" height="100" className="rounded border"/>
                                </div>
                            )}
                        </div>

                        {/* Content */}
                        <div className="mb-3">
                            <label className="form-label fw-500">Content</label>
                            {editorLoaded && (
                                <CKEditor
                                    editor={ClassicEditor}
                                    data={values.content}
                                    onChange={(event, editor) => {
                                        setFieldValue("content", editor.getData());
                                    }}
                                />
                            )}
                            <ErrorMessage name="content" component="div" className="text-danger fs-12" />
                        </div>

                        {/* Buttons */}
                        <div className="text-end">
                            <button type="submit" className="btn btn-success me-2" disabled={isLoading}>
                                {isLoading ? (
                                    <>
                                    <Spinner as="span" animation="border" size="sm" />{' '}&nbsp;
                                    Saving...</>
                                )  : "Save as Draft"}
                            </button>
                            <button type="submit" className="btn btn-success" disabled={isPLoading} onClick={() => setPublishStatus("published")}>
                                {isPLoading ?  (
                                    <>
                                    <Spinner as="span" animation="border" size="sm" />{' '}&nbsp;
                                    Saving...</>
                                )  : "Save as Published"}
                            </button>
                        </div>
                    </Form>
                )}
            </Formik>
            <Modal show={showModal} onHide={() => setShowModal(false)} backdrop="static" keyboard={false}>
                <Modal.Header closeButton>
                    <Modal.Title>Blog Submitted</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    Your blog has been submitted for review. After verification, it will be live on the site.
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="primary" onClick={() => { setShowModal(false); navigate("/coach/my-blogs"); }}>
                        OK
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
}

export default DraftComponent;
